import { useEffect, useMemo, useState } from "react";
import { disconnectFranchise, getConnectedFranchises, updateFranchiseStatus } from "../../services";
import { showError } from "../../helpers";
import { Button } from "@jobber/components/Button";
import { ConfirmationModal } from "@jobber/components/ConfirmationModal";
import { DataTable, SortDirection } from "@jobber/components/DataTable";
import { StatusLabel } from "@jobber/components/StatusLabel";
import { Typography } from "@jobber/components/Typography";
import { Franchise, FranchiseStatus } from "../../types/Franchise";
import DashboardTab from "../../components/Dashboard/DashboardTab";

type SortColumn = "name" | "status" | "date_approved" | "jobs_synced" | "listen360_location";

function Franchises() {
    const [connectedFranchises, setConnectedFranchises] = useState(Array<Franchise>);
    const [sortColumn, setSortColumn] = useState<SortColumn | null>("status");
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ascending);
    const [hoveredRow, setHoveredRow] = useState<string | null>(null);
    const [disconnectTarget, setDisconnectTarget] = useState<Franchise | null>(null);

    const nextDirection = {
        [SortDirection.equilibrium]: SortDirection.ascending,
        [SortDirection.ascending]: SortDirection.descending,
        [SortDirection.descending]: SortDirection.equilibrium,
    };

    const handleSort = (column: SortColumn) => {
        const next = sortColumn === column
            ? nextDirection[sortDirection]
            : SortDirection.ascending;
        setSortColumn(next === SortDirection.equilibrium ? null : column);
        setSortDirection(next);
    };

    const getDirection = (column: SortColumn) =>
        sortColumn === column ? sortDirection : SortDirection.equilibrium;

    const STATUS_ORDER: Record<string, number> = { PENDING: 0, ACTIVE: 1, INACTIVE: 2 };

    const sortKeyFn: Record<SortColumn, (f: Franchise) => string | number> = {
        name: f => f.name.toLowerCase(),
        status: f => STATUS_ORDER[f.status] ?? 3,
        date_approved: f => f.date_approved ?? "",
        jobs_synced: f => parseInt(f.jobs_synced) || 0,
        listen360_location: f => (f.listen360_location ?? "").toLowerCase(),
    };

    const sortedFranchises = useMemo(() => {
        if (!sortColumn || sortDirection === SortDirection.equilibrium) {
            return connectedFranchises;
        }

        const getValue = sortKeyFn[sortColumn];
        const dir = sortDirection === SortDirection.ascending ? 1 : -1;

        return [...connectedFranchises].sort((a, b) => {
            const aVal = getValue(a);
            const bVal = getValue(b);
            const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            if (cmp !== 0) return cmp * dir;

            // secondary sort: alphabetical by name
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();
            return aName < bName ? -1 : aName > bName ? 1 : 0;
        });
    }, [connectedFranchises, sortColumn, sortDirection]);

    // load up connected accounts
    useEffect(()=> {
        (async () => {
            try {
               setConnectedFranchises(await getConnectedFranchises());
            } catch(e: any) {
                showError("Error fetching connected franchises: " + e.message);
            }
        })();
    }, []);

    function status_to_label(status: FranchiseStatus): "success" | "inactive" | "warning" {
        switch (status) {
            case FranchiseStatus.Active:
                return "success";
            case FranchiseStatus.Inactive:
                return "inactive";
            case FranchiseStatus.Pending:
                return "warning"
        }
    }

    async function handleActivate(row: Franchise) {
        try {
            await updateFranchiseStatus(row.id, row.status, FranchiseStatus.Active);
            const today = new Date().toISOString().split("T")[0];
            setConnectedFranchises(prev => prev.map(f =>
                f.id === row.id
                    ? {
                        ...f,
                        status: FranchiseStatus.Active as const, ...(row.status === FranchiseStatus.Pending && {date_approved: today})
                    }
                    : f
            ));
        } catch (e: any) {
            showError("Error activating franchise: " + e.message);
        }
    }

    async function handleDeactivate(row: Franchise) {
        try {
            await updateFranchiseStatus(row.id, row.status, FranchiseStatus.Inactive);
            setConnectedFranchises(prev => prev.map(f =>
                f.id === row.id ? {...f, status: FranchiseStatus.Inactive as const} : f
            ));
        } catch (e: any) {
            showError("Error deactivating franchise: " + e.message);
        }
    }

    async function handleDisconnect() {
        const target = disconnectTarget!;
        setDisconnectTarget(null);
        try {
            await disconnectFranchise(target.id);
            setConnectedFranchises(prev => prev.filter(f => f.id !== target.id));
        } catch (e: any) {
            showError("Error disconnecting franchise: " + e.message);
        }
    }

    const columns: { key: SortColumn; label: string; align?: "right" }[] = [
        { key: "name", label: "Franchise" },
        { key: "status", label: "Status" },
        { key: "listen360_location", label: "Listen360 Location" },
        { key: "jobs_synced", label: "Jobs Synced", align: "right" },
        { key: "date_approved", label: "Date Approved", align: "right" },
    ];

    return (
        <DashboardTab>
            <DataTable.Container>
                <DataTable.Table>
                    <style>{`th[data-align="right"] > button { justify-content: flex-end; }`}</style>
                    <DataTable.Header>
                        {columns.map(({ key, label, align }) => (
                            <DataTable.SortableHeader
                                key={key}
                                direction={getDirection(key)}
                                onSort={() => handleSort(key)}
                                {...(align && { "data-align": align })}
                            >
                                {label}
                            </DataTable.SortableHeader>
                        ))}
                    </DataTable.Header>
                    <DataTable.Body>
                        {sortedFranchises.map((row) => (
                            <DataTable.Row
                                key={row.id}
                                onMouseEnter={() => setHoveredRow(row.id)}
                                onMouseLeave={() => setHoveredRow(null)}
                            >
                                <DataTable.Cell>
                                    <Typography fontWeight="bold">{row.name}</Typography>
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    <StatusLabel status={status_to_label(row.status)} label={row.status.toLowerCase()}/>
                                </DataTable.Cell>
                                <DataTable.Cell>{row.listen360_location ?? "--"}</DataTable.Cell>
                                <DataTable.Cell>
                                    <div style={{ textAlign: "right", fontFamily: "monospace" }}>{row.jobs_synced}</div>
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    <div style={{ textAlign: "right", fontFamily: "monospace" }}>
                                        {row.status !== FranchiseStatus.Pending && row.date_approved
                                            ? new Date(row.date_approved).toISOString().slice(0, 10)
                                            : "--"}
                                    </div>
                                </DataTable.Cell>
                                <DataTable.Cell></DataTable.Cell>
                                <div
                                    style={{
                                        opacity: hoveredRow === row.id ? 1 : 0,
                                        transition: "opacity 0.2s ease",
                                        width: "100%",
                                    }}
                                >
                                    <DataTable.RowActions>
                                        {row.status !== FranchiseStatus.Active && (
                                            <Button size="small" icon="checkmarkCircle" label="Activate" onClick={() => handleActivate(row)} />
                                        )}
                                        {row.status === FranchiseStatus.Active && (
                                            <Button size="small" icon="circle" label="Deactivate" variation="subtle" onClick={() => handleDeactivate(row)} />
                                        )}
                                        {(row.status === FranchiseStatus.Inactive || row.status === FranchiseStatus.Pending) && (
                                            <Button size="small" icon="trash" variation="destructive" label={row.status === FranchiseStatus.Pending ? "Reject" : "Disconnect"} onClick={() => setDisconnectTarget(row)} />
                                        )}
                                    </DataTable.RowActions>
                                </div>
                            </DataTable.Row>
                        ))}
                    </DataTable.Body>
                </DataTable.Table>
            </DataTable.Container>

            <ConfirmationModal
                open={disconnectTarget !== null}
                title={disconnectTarget?.status === FranchiseStatus.Pending
                    ? `Reject Franchise: ${disconnectTarget?.name}`
                    : `Disconnect Franchise: ${disconnectTarget?.name}`}
                confirmLabel={disconnectTarget?.status === FranchiseStatus.Pending ? "Reject" : "Disconnect"}
                variation="destructive"
                onConfirm={handleDisconnect}
                onRequestClose={() => setDisconnectTarget(null)}
                message={`Are you sure you want to ${disconnectTarget?.status === FranchiseStatus.Pending ? "reject" : "disconnect"} this franchise? This action cannot be undone, and the franchise owner will have to reconnect the integration from Jobber in order to start syncing again.`}
            />
        </DashboardTab>
    );
}

export default Franchises;
